import React, { useEffect, useRef } from "react";
import { INVESTOR_CERTIFICATION_TEXT } from "../AccreditedInvestorGate";
import { GateNudgeAnnouncement, GateNudgeRings } from "../GateNudge";
import GoogleSignInButton from "../GoogleSignInButton";
import { useHomeGateFlow } from "../../hooks/useHomeGateFlow";
import configService from "../../services/configService";
import { isXInAppBrowser } from "../../utils/inAppBrowser";

export const HOME_GATE_ID = "home-gate";
export const HOME_GATE_EMAIL_ID = "home-gate-email";
const OTP_ID = "home-gate-otp";
const CERTIFICATION_NOTICE_ID = "home-gate-certification-notice";
const ERROR_ID = "home-gate-error";
// React 19 knows `inert` as a boolean attribute; spread so the typings in use do not have to.
const INERT = { inert: true } as Record<string, boolean>;

const Arrow = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 17 17 7M9 7h8v8" /></svg>
);

const cooldownLabel = (seconds: number) => `${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, "0")}`;

/**
 * The share page's gate, in the hero: Google, email, and the certification
 * notice. The 6-digit code takes the same spot. Every sign-up
 * CTA on the page jumps here instead of leaving for the login page.
 */
const HomeGate: React.FC<{ campaign: string; section: string }> = ({ campaign, section }) => {
  const flow = useHomeGateFlow({ campaign, section });
  const otpRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (flow.step === "code") otpRef.current?.focus({ preventScroll: true });
  }, [flow.step]);

  // Google beside the email, as on the share gate (James, 2026-10-09). Not
  // inside the X app's browser, where Google refuses to complete, and not
  // without a configured client.
  const googleAvailable = configService.isGoogleSignInEnabled()
    && Boolean(configService.getGoogleClientId())
    && !isXInAppBrowser();
  const googleLocked = flow.isSubmitting;
  // Only request failures render an error; missing input is announced by the nudge.
  const emailError = flow.error;
  const certificationNotice = (
    <p id={CERTIFICATION_NOTICE_ID} className="text-[6px] leading-[9px] text-v7-white/70 dark:text-v7-white/70">
      By signing in, you are certifying: {INVESTOR_CERTIFICATION_TEXT}
    </p>
  );

  if (flow.step === "code") {
    return (
      <form
        id={HOME_GATE_ID}
        className="home-gate"
        noValidate
        data-gate-step="code"
        onSubmit={(event) => { event.preventDefault(); void flow.submitCode(); }}
        aria-label="Enter your code"
        aria-describedby={flow.error ? `${CERTIFICATION_NOTICE_ID} ${ERROR_ID}` : CERTIFICATION_NOTICE_ID}
      >
        <p className="home-gate-lead">
          We sent a 6-digit code to <b>{flow.email}</b>.{" "}
          <button type="button" className="home-gate-link" onClick={flow.editEmail} disabled={flow.isSubmitting}>Edit it</button>
        </p>
        <div className="home-gate-row">
          <label className="sr-only" htmlFor={OTP_ID}>One-time code</label>
          <input
            id={OTP_ID}
            ref={otpRef}
            className="home-gate-input home-gate-input--code"
            name="otp"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            required
            placeholder="······"
            value={flow.otp}
            onChange={(event) => flow.setOtp(event.target.value)}
            disabled={flow.isSubmitting}
            aria-invalid={Boolean(flow.error)}
          />
          <button type="submit" className="home-gate-submit" disabled={flow.isSubmitting || flow.otp.length !== 6}>
            {flow.isSubmitting ? "Verifying" : "Continue"}
            {!flow.isSubmitting && <Arrow />}
          </button>
        </div>
        {flow.error && <p className="home-gate-error" id={ERROR_ID} role="alert">{flow.error}</p>}
        {certificationNotice}
        <p className="home-gate-note">
          Didn&apos;t get it?{" "}
          <button
            type="button"
            className="home-gate-link"
            onClick={() => void flow.resendCode()}
            disabled={flow.isSubmitting || flow.resendCooldown > 0}
          >
            {flow.resendCooldown > 0 ? `Send it again in ${cooldownLabel(flow.resendCooldown)}` : "Send it again"}
          </button>
        </p>
      </form>
    );
  }

  return (
    <form
      id={HOME_GATE_ID}
      className="home-gate"
      noValidate
      data-gate-step="email"
      onSubmit={(event) => { event.preventDefault(); void flow.submitEmail(); }}
      aria-label="Create your account"
      aria-describedby={emailError ? `${CERTIFICATION_NOTICE_ID} ${ERROR_ID}` : CERTIFICATION_NOTICE_ID}
    >
      {googleAvailable && (
        <>
          <div className="home-gate-google">
            {/* Google's iframe cannot be disabled directly. Keep its wrapper inert while a code request is in flight. */}
            <div {...(googleLocked ? INERT : {})} aria-hidden={googleLocked || undefined}>
              <GoogleSignInButton text="continue_with" theme="outline" onClickIntent={flow.engage} onSuccess={flow.completeGoogleSignIn} />
            </div>
          </div>
          <div className="home-gate-or" aria-hidden="true"><span>or with email</span></div>
        </>
      )}
      <div className="home-gate-row">
        <div className="relative flex min-w-0 flex-1 rounded-v7-control">
          <label className="sr-only" htmlFor={HOME_GATE_EMAIL_ID}>Email address</label>
          <input
            id={HOME_GATE_EMAIL_ID}
            className={`home-gate-input ${flow.nudge.reason === "email" ? "gate-nudged" : ""}`}
            name="email"
            type="email"
            autoComplete="email"
            required
            placeholder="you@email.com"
            value={flow.email}
            onFocus={flow.engage}
            onChange={(event) => flow.setEmail(event.target.value)}
            disabled={flow.isSubmitting}
            aria-invalid={flow.nudge.reason === "email" || Boolean(emailError)}
          />
          <GateNudgeRings active={flow.nudge.reason === "email"} sequence={flow.nudge.sequence} />
        </div>
        <button type="submit" className="home-gate-submit" disabled={flow.isSubmitting}>
          {flow.isSubmitting ? "Sending code" : "Continue"}
          {!flow.isSubmitting && <Arrow />}
        </button>
      </div>
      {emailError && <p className="home-gate-error" id={ERROR_ID} role="alert">{emailError}</p>}
      {certificationNotice}
      <GateNudgeAnnouncement reason={flow.nudge.reason} sequence={flow.nudge.sequence} />
    </form>
  );
};

export default HomeGate;
